/* MolCellQuest 問題データ — 免疫・生体防御
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "cell", category: "immunity", categoryName: "免疫・生体防御" };

export const CELL_IMMUNITY = [
  {
    id: "imm01", ...CAT,
    title: "自然免疫と獲得免疫の違い", diff: 100, tags: ["自然免疫", "獲得免疫", "免疫記憶"],
    explain: `<p>ヒトの免疫は<b>自然免疫</b>と<b>獲得（適応）免疫</b>の2段構えになっている。
              自然免疫の特徴として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "病原体を初めて認識してから効果を発揮するまで、数日〜1週間かかる。",
      "生まれつき備わっており、病原体に共通する特徴をすばやく認識して即座に働く。",
      "抗体を作り出し、同じ病原体に対する記憶を残す。",
      "T細胞やB細胞が中心となり、抗原ごとに特異的な受容体で応答する。",
    ],
    answer: 1,
    hints: [
      "「自然」は英語で innate、つまり生まれつき持っているという意味です。",
      "反応の速さと、特異性・記憶の有無で2つを見分けます。",
    ],
    solution: `<p><b>【ステップ1】2つの免疫を対比する</b></p>
               <table>
                 <tr><th></th><th>自然免疫</th><th>獲得（適応）免疫</th></tr>
                 <tr><td>いつから</td><td><b>生まれつき</b></td><td>感染後に成立</td></tr>
                 <tr><td>速さ</td><td><b>即時（数分〜数時間）</b></td><td>遅い（数日〜1週間）</td></tr>
                 <tr><td>特異性</td><td>低い（共通パターンを認識）</td><td><b>高い（抗原ごと）</b></td></tr>
                 <tr><td>記憶</td><td>なし</td><td><b>あり</b></td></tr>
                 <tr><td>主な担い手</td><td>好中球・マクロファージ・NK細胞・補体</td><td>T細胞・B細胞・抗体</td></tr>
               </table>
               <p><b>【ステップ2】自然免疫の「認識」のしかた</b></p>
               <p>自然免疫は、細菌の細胞壁成分やウイルスの二本鎖RNAなど、
               <b>多くの病原体に共通するパターン</b>（PAMPs）を
               Toll様受容体（TLR）などの<b>パターン認識受容体</b>で捉えます。
               だから「初めて出会う相手」でもすぐ反応できる代わりに、細かい見分けは苦手です。</p>
               <p><b>【ステップ3】選択肢の吟味</b></p>
               <ul>
                 <li>A・C・D はいずれも<b>獲得免疫</b>の特徴です。</li>
                 <li>B が自然免疫の本質（生まれつき・即時・共通パターン認識）を正しく述べています。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm02", ...CAT,
    title: "感染を防ぐ最初の壁", diff: 115, tags: ["物理的バリア", "粘膜", "リゾチーム"],
    explain: `<p>病原体の侵入を防ぐ「最初の壁」は、免疫細胞が働く前の
              <b>物理的・化学的バリア</b>である。このバリアに<b>当てはまらない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "皮膚の角質層が物理的に病原体の侵入を防ぐ。",
      "涙や唾液に含まれるリゾチームが細菌の細胞壁を分解する。",
      "胃の強い酸性（胃酸）が多くの微生物を殺す。",
      "抗体が病原体に結合して中和する。",
    ],
    answer: 3,
    hints: [
      "「バリア」は免疫細胞が動員される前の、体の構造や分泌物による防御です。",
      "抗体はどの免疫（自然か獲得か）で作られるものでしょうか。",
    ],
    solution: `<p><b>【ステップ1】バリアの3タイプ</b></p>
               <table>
                 <tr><th>種類</th><th>例</th></tr>
                 <tr><td>物理的</td><td>皮膚の角質層、粘膜、線毛による異物の排出</td></tr>
                 <tr><td>化学的</td><td>胃酸、皮脂の脂肪酸、リゾチーム（涙・唾液）</td></tr>
                 <tr><td>生物学的</td><td>常在細菌叢が病原体の定着を妨げる</td></tr>
               </table>
               <p><b>【ステップ2】リゾチームの働き</b></p>
               <p>リゾチームは細菌細胞壁の<b>ペプチドグリカン</b>を加水分解する酵素で、
               涙・唾液・鼻汁などに含まれます。ここを突破されて初めて自然免疫の細胞が出動します。</p>
               <p><b>【ステップ3】なぜ D が違うか</b></p>
               <p>抗体（免疫グロブリン）は<b>獲得免疫</b>でB細胞が作る特異的な分子で、
               「最初の壁」ではありません。バリアを越えた病原体に、時間をかけて後から効いてくるものです。</p>
               <p>したがって、正解は <b>D</b> です。</p>`,
  },

  {
    id: "imm03", ...CAT,
    title: "食細胞による貪食", diff: 135, tags: ["マクロファージ", "好中球", "貪食", "ファゴサイトーシス"],
    explain: `<p>好中球やマクロファージは、病原体を細胞内に取り込んで分解する。
              この過程について正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "病原体をエキソサイトーシスで細胞外へ放出して処理する。",
      "取り込んだ病原体を含む食胞（ファゴソーム）がリソソームと融合し、加水分解酵素で分解する。",
      "取り込みは特別なタンパク質を必要とせず、単純拡散で膜を通過させる。",
      "分解された断片はすべて完全に処分され、他の免疫細胞には一切伝えられない。",
    ],
    answer: 1,
    hints: [
      "「食べて」から「消化する」には、どのオルガネラと合体する必要があるでしょう。",
      "貪食（phagocytosis）はエンドサイトーシスの一種です。",
    ],
    solution: `<p><b>【ステップ1】貪食の流れ</b></p>
               <ol>
                 <li>食細胞が病原体を認識し、細胞膜を伸ばして包み込む（<b>ファゴサイトーシス</b>）</li>
                 <li>病原体を含む<b>ファゴソーム（食胞）</b>ができる</li>
                 <li>ファゴソームが<b>リソソーム</b>と融合してファゴリソソームになる</li>
                 <li>加水分解酵素・活性酸素・低pHで病原体を<b>分解</b></li>
               </ol>
               <p><b>【ステップ2】オプソニン化という後押し</b></p>
               <p>病原体が抗体や補体（C3b）で覆われると、食細胞はそれを目印にして
               ずっと効率よく取り込めます。これを<b>オプソニン化</b>といい、
               自然免疫と獲得免疫をつなぐ仕組みです。</p>
               <p><b>【ステップ3】ただ食べるだけではない</b></p>
               <p>マクロファージや樹状細胞は、分解した断片（抗原ペプチド）を
               <b>MHC分子にのせて細胞表面に提示</b>し、T細胞に情報を渡します（抗原提示）。
               だから選択肢Dの「一切伝えない」は誤りです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm04", ...CAT,
    title: "抗体の基本構造", diff: 150, tags: ["抗体", "免疫グロブリン", "可変領域", "抗原結合部位"],
    explain: `<p>抗体（免疫グロブリン）の基本構造は、2本のH鎖（重鎖）と2本のL鎖（軽鎖）が
              ジスルフィド結合で結ばれたY字型をしている。抗体の説明として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "抗原と結合する部位は、アミノ酸配列がどの抗体でも一定な「定常領域」にある。",
      "1個の抗体分子には抗原結合部位が1か所だけある。",
      "抗原と結合する部位は、H鎖とL鎖の「可変領域」がつくり、Y字の先端に2か所ある。",
      "抗体は糖脂質でできており、タンパク質は含まない。",
    ],
    answer: 2,
    hints: [
      "Y字の「腕の先」と「根元」で役割が違います。多様性が必要なのはどちらでしょう。",
      "Y字は2本の腕を持ちます。それぞれの先に何があるでしょう。",
    ],
    solution: `<p><b>【ステップ1】Y字を部品に分ける</b></p>
               <table>
                 <tr><th>領域</th><th>場所</th><th>役割</th></tr>
                 <tr><td><b>可変領域（V）</b></td><td>Y字の<b>先端</b></td><td>抗原と結合。抗体ごとに配列が異なる</td></tr>
                 <tr><td><b>定常領域（C）</b></td><td>Y字の<b>根元（Fc）</b></td><td>食細胞や補体との結合、クラスを決める</td></tr>
               </table>
               <p><b>【ステップ2】抗原結合部位は2か所</b></p>
               <p>H鎖とL鎖の可変領域が組み合わさって<b>1つの抗原結合部位</b>を作り、
               Y字には腕が2本あるので<b>合計2か所</b>で抗原に結合できます（IgGの場合）。
               これにより病原体どうしを橋渡しして<b>凝集</b>させられます。</p>
               <p><b>【ステップ3】選択肢の吟味</b></p>
               <ul>
                 <li>A ⇒ 結合部位は<b>可変領域</b>。定常領域ではない。</li>
                 <li>B ⇒ 結合部位は<b>2か所</b>（IgMなら10か所）。</li>
                 <li>D ⇒ 抗体は<b>タンパク質</b>（糖鎖が少し付く糖タンパク質）。</li>
               </ul>
               <p>したがって、正解は <b>C</b> です。</p>`,
  },

  {
    id: "imm05", ...CAT,
    title: "液性免疫と細胞性免疫", diff: 185, tags: ["B細胞", "T細胞", "液性免疫", "細胞性免疫"],
    explain: `<p>獲得免疫は<b>液性免疫</b>と<b>細胞性免疫</b>に大別される。
              ウイルスに感染してしまった<b>自分の細胞</b>を排除するのに主に働くのはどちらで、
              その主役はどの細胞か。最も適切なものを選べ。</p>`,
    type: "radio",
    options: [
      "液性免疫。B細胞が抗体を出して感染細胞ごと溶かす。",
      "細胞性免疫。キラーT細胞（CD8陽性T細胞）が感染細胞を直接攻撃する。",
      "液性免疫。ヘルパーT細胞が感染細胞に穴をあける。",
      "細胞性免疫。マクロファージが抗体を放出して感染細胞を破壊する。",
    ],
    answer: 1,
    hints: [
      "抗体は血液や体液（＝液性）を漂う分子で、細胞の「中」には入れません。",
      "細胞の中に隠れたウイルスを片付けるには、感染細胞ごと壊す必要があります。",
    ],
    solution: `<p><b>【ステップ1】2つの免疫の守備範囲</b></p>
               <table>
                 <tr><th></th><th>液性免疫</th><th>細胞性免疫</th></tr>
                 <tr><td>主役</td><td><b>B細胞 →抗体</b></td><td><b>キラーT細胞（CD8）</b></td></tr>
                 <tr><td>得意な相手</td><td>体液中の細菌・毒素・ウイルス（細胞外）</td><td><b>感染細胞・がん細胞（細胞内）</b></td></tr>
                 <tr><td>攻撃法</td><td>抗体で中和・凝集・オプソニン化</td><td>感染細胞を直接殺す（アポトーシス誘導）</td></tr>
               </table>
               <p><b>【ステップ2】なぜ抗体では足りないか</b></p>
               <p>抗体は<b>細胞膜を通れない</b>ので、細胞の中に入り込んだウイルスには手が届きません。
               そこで、ウイルスを産生している感染細胞そのものを<b>キラーT細胞</b>が見つけ出し、
               パーフォリンやグランザイムでアポトーシスを起こして排除します。</p>
               <p><b>【ステップ3】ヘルパーT細胞の位置づけ</b></p>
               <p>ヘルパーT細胞（CD4）は自分で攻撃はしませんが、
               B細胞にもキラーT細胞にも指令を出す<b>司令塔</b>で、両方の免疫を支えます。
               （選択肢Cの「穴をあける」のはキラーT細胞の役割で、ヘルパーT細胞ではありません。）</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm06", ...CAT,
    title: "MHCと抗原提示", diff: 205, tags: ["MHC", "抗原提示", "CD4", "CD8", "樹状細胞"],
    explain: `<p>T細胞は、抗原を単独では認識できず、<b>MHC分子にのせて提示された</b>抗原ペプチドを認識する。
              MHCクラスIとクラスIIについて正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "MHCクラスIはほぼすべての有核細胞にあり、細胞内で作られた抗原をCD8陽性（キラー）T細胞に提示する。",
      "MHCクラスIは抗原提示細胞にしかなく、CD4陽性（ヘルパー）T細胞に提示する。",
      "MHCクラスIIはすべての細胞にあり、細胞内の抗原をキラーT細胞に提示する。",
      "MHCクラスIもIIも、T細胞ではなくB細胞だけに抗原を提示する。",
    ],
    answer: 0,
    hints: [
      "クラスIは「自分の中で何が作られているか」を全細胞が申告する仕組みです。",
      "『I とキラー(CD8)』『II とヘルパー(CD4)』——数字と相手の対応を覚えます（8＝1×2＋…と語呂で覚える人も）。",
    ],
    solution: `<p><b>【ステップ1】2つのMHCを対比する</b></p>
               <table>
                 <tr><th></th><th>MHCクラスI</th><th>MHCクラスII</th></tr>
                 <tr><td>どこにある</td><td><b>ほぼ全ての有核細胞</b></td><td>樹状細胞・マクロファージ・B細胞（<b>抗原提示細胞</b>）</td></tr>
                 <tr><td>のせる抗原</td><td><b>細胞内</b>で作られたもの（ウイルス・自己）</td><td><b>細胞外</b>から取り込んだもの</td></tr>
                 <tr><td>提示する相手</td><td><b>CD8（キラー）T細胞</b></td><td><b>CD4（ヘルパー）T細胞</b></td></tr>
                 <tr><td>意味</td><td>「私の中身は正常か」の申告</td><td>「外敵を捕まえた」という報告</td></tr>
               </table>
               <p><b>【ステップ2】理にかなった設計</b></p>
               <p>ウイルスに感染するとクラスIに<b>ウイルス由来ペプチド</b>が載り、
               それを見たキラーT細胞が感染細胞を排除します。
               全細胞がクラスIを持つのは、どの細胞が感染しても申告できるようにするためです。</p>
               <p><b>【ステップ3】語呂で固定する</b></p>
               <p><b>CD8 × MHC-I</b>、<b>CD4 × MHC-II</b>。
               掛け算するとどちらも 8 になる、という覚え方が有名です。</p>
               <p>選択肢A はクラスIの説明として完全に正しく、B・C は数字と役割が入れ替わっており、
               D も誤りです。したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "imm07", ...CAT,
    title: "クローン選択説", diff: 220, tags: ["クローン選択説", "リンパ球", "抗原特異性", "クローン増殖"],
    explain: `<p>1個のリンパ球（B細胞やT細胞）は<b>1種類の抗原</b>にしか反応できない受容体をもつ。
              では、体は無数の病原体にどう対応しているのか。<b>クローン選択説</b>の考え方として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "抗原が入ってきてから、その抗原に合う受容体を1個のリンパ球が新たに作り始める。",
      "あらかじめ多様な受容体をもつリンパ球が用意されており、抗原に合うものだけが選ばれて増殖する。",
      "すべてのリンパ球が同じ受容体をもち、どんな抗原にも同じように反応する。",
      "抗原が受容体の形を鋳型のように変えて、リンパ球に結合できるようにする。",
    ],
    answer: 1,
    hints: [
      "「選択」という言葉がヒント。作ってから選ぶのか、先に用意しておいて選ぶのか。",
      "受容体の多様性は、抗原に出会う『前』に用意されています。",
    ],
    solution: `<p><b>【ステップ1】クローン選択説の骨子</b></p>
               <ol>
                 <li>抗原に出会う<b>前</b>から、体には多種多様な受容体をもつリンパ球が<b>1個ずつ</b>用意されている。</li>
                 <li>ある抗原が侵入すると、その抗原にぴったり合う受容体をもつリンパ球<b>だけ</b>が選ばれて活性化する。</li>
                 <li>選ばれたリンパ球が<b>分裂・増殖（クローン増殖）</b>し、同じ受容体をもつ細胞の集団になる。</li>
                 <li>一部は<b>記憶細胞</b>として残る。</li>
               </ol>
               <p><b>【ステップ2】「作ってから」ではなく「選んでから」</b></p>
               <p>ポイントは、抗原が受容体を<b>作らせる</b>のでも<b>形を変える</b>のでもないこと。
               受容体の多様性は遺伝子の再構成であらかじめ生み出されており、
               抗原はその中から適合するものを<b>選ぶ</b>だけです。これが「選択」の意味です。</p>
               <p><b>【ステップ3】鋳型説との対比（歴史）</b></p>
               <p>かつては抗原を鋳型にして抗体の形が決まるという「鋳型説（選択肢D）」が提案されましたが、
               バーネットのクローン選択説に取って代わられました。
               免疫記憶や自己寛容をうまく説明できたためです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm08", ...CAT,
    title: "一次応答・二次応答とワクチン", diff: 235, tags: ["免疫記憶", "二次応答", "記憶細胞", "ワクチン"],
    explain: `<p>同じ抗原に2回目に出会うと、1回目より<b>速く・強く</b>抗体が作られる。
              この現象（二次応答）とワクチンの原理について、正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "二次応答が速く強いのは、1回目に作られた抗体が体内にずっと残っているからだけである。",
      "二次応答では、1回目に作られた記憶細胞が待機しており、再び同じ抗原が来ると急速に増殖して大量の抗体を作る。",
      "ワクチンは病原体そのものを増やして体内で発症させ、治ることで免疫をつける。",
      "二次応答では主にIgEが最初に大量に作られる。",
    ],
    answer: 1,
    hints: [
      "抗体の量は時間とともに減っていきます。では「速く強く」の主因は何でしょう。",
      "ワクチンは『病気にならずに』記憶だけを作るのが目的です。",
    ],
    solution: `<p><b>【ステップ1】一次応答と二次応答</b></p>
               <table>
                 <tr><th></th><th>一次応答（初回）</th><th>二次応答（再会）</th></tr>
                 <tr><td>反応までの時間</td><td>遅い（1週間ほど）</td><td><b>速い（数日）</b></td></tr>
                 <tr><td>抗体量</td><td>少ない</td><td><b>多い</b></td></tr>
                 <tr><td>主な抗体クラス</td><td>まずIgM、遅れてIgG</td><td><b>最初から大量のIgG</b></td></tr>
                 <tr><td>担い手</td><td>ナイーブなB・T細胞</td><td><b>記憶細胞</b></td></tr>
               </table>
               <p><b>【ステップ2】記憶細胞がカギ</b></p>
               <p>二次応答が速く強い主な理由は、1回目に作られた<b>記憶B細胞・記憶T細胞</b>が
               長期間待機しているからです。抗体そのものは時間とともに減りますが、
               記憶細胞が残っていれば再会時にすぐ増殖できます（選択肢A が不十分な理由）。</p>
               <p><b>【ステップ3】ワクチンの原理</b></p>
               <p>ワクチンは、<b>弱毒化・不活化した病原体やその一部（抗原）</b>を投与して、
               <b>発症させずに</b>一次応答と記憶細胞の形成だけを起こします。
               本物が来たときには二次応答で素早く撃退できます。
               病気にして治す（選択肢C）わけではありません。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm09", ...CAT,
    title: "補体系の働き", diff: 245, tags: ["補体", "自然免疫", "膜侵襲複合体", "オプソニン化"],
    explain: `<p>血しょう中には<b>補体</b>と呼ばれるタンパク質群が存在し、活性化すると連鎖的に働く。
              補体の働きとして<b>適切でない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "病原体の表面に結合し、食細胞が取り込みやすくする（オプソニン化）。",
      "膜侵襲複合体（MAC）を作って病原体の膜に穴をあけ、溶菌する。",
      "炎症を促し、免疫細胞を感染部位に呼び寄せる。",
      "抗原ペプチドをMHCにのせてT細胞に提示し、抗原特異的な記憶を作る。",
    ],
    answer: 3,
    hints: [
      "補体は主に自然免疫側で働くタンパク質群です。抗原提示や記憶は誰の仕事でしょう。",
      "3つは『病原体を直接どうにかする』話、1つは『情報を伝えて記憶を作る』話です。",
    ],
    solution: `<p><b>【ステップ1】補体の三大機能</b></p>
               <table>
                 <tr><th>機能</th><th>内容</th></tr>
                 <tr><td><b>オプソニン化</b></td><td>C3b が病原体を覆い、食細胞の貪食を助ける</td></tr>
                 <tr><td><b>溶菌</b></td><td>C5b〜C9 が<b>膜侵襲複合体（MAC）</b>を作り膜に穴をあける</td></tr>
                 <tr><td><b>炎症誘導</b></td><td>C3a・C5a が肥満細胞などを刺激し、免疫細胞を呼び寄せる</td></tr>
               </table>
               <p><b>【ステップ2】補体はどの免疫か</b></p>
               <p>補体は基本的に<b>自然免疫</b>のタンパク質群で、
               生まれつき血中に存在し、病原体表面や抗体をきっかけに<b>連鎖反応（カスケード）</b>で活性化します。
               自然免疫と獲得免疫（抗体）をつなぐ橋渡し役でもあります。</p>
               <p><b>【ステップ3】なぜ D が誤りか</b></p>
               <p>抗原ペプチドをMHCにのせてT細胞に提示し、抗原特異的な記憶を作るのは
               <b>樹状細胞やマクロファージなどの抗原提示細胞とリンパ球</b>の仕事であって、
               補体タンパク質の機能ではありません。</p>
               <p>したがって、適切でないものは <b>D</b> です。</p>`,
  },

  {
    id: "imm10", ...CAT,
    title: "抗体の多様性はどう生まれるか", diff: 280, tags: ["VDJ組換え", "遺伝子再構成", "抗体多様性", "利根川進"],
    explain: `<p>ヒトのゲノムの遺伝子数は約2万個なのに、私たちは10<sup>10</sup> 種類を超えるともいわれる
              多様な抗体を作れる。この<b>抗体の多様性</b>を生み出す主な仕組みはどれか。</p>`,
    type: "radio",
    options: [
      "抗体をコードする1個の完成した遺伝子が、体細胞分裂のたびにランダムに点変異を起こすから。",
      "V・D・J という複数の遺伝子断片群から1個ずつ選んでつなぎ合わせる遺伝子再構成が、B細胞ごとに起こるから。",
      "1個の抗体遺伝子から、選択的スプライシングだけで無限に異なるアミノ酸配列が作られるから。",
      "抗原が入ってくるたびに、その形に合わせて新しい抗体遺伝子がゲノムに書き加えられるから。",
    ],
    answer: 1,
    hints: [
      "限られた『部品』を組み合わせると、掛け算で膨大なパターンが作れます。",
      "利根川進がノーベル賞を受けたテーマです。ゲノムの一部が細胞ごとに『組み替わり』ます。",
    ],
    solution: `<p><b>【ステップ1】組み合わせが多様性を生む</b></p>
               <p>抗体の可変領域は、あらかじめゲノムに用意された
               <b>V（Variable）・D（Diversity）・J（Joining）</b>という
               遺伝子断片の<b>群</b>から、B細胞が成熟する過程で1個ずつ選んでつなぎます
               （<b>V(D)J組換え</b>）。断片の組み合わせだけで莫大な種類が生まれます。</p>
               <p><b>【ステップ2】多様性を上乗せする要素</b></p>
               <ul>
                 <li><b>連結部の多様性</b>：断片をつなぐ際に塩基が挿入・削除される。</li>
                 <li><b>H鎖とL鎖の組み合わせ</b>：それぞれ独立に再構成され、掛け算で増える。</li>
                 <li><b>体細胞高頻度変異</b>：抗原刺激後、可変領域に変異が入り親和性が上がる（親和性成熟）。</li>
               </ul>
               <p><b>【ステップ3】なぜ他が誤りか</b></p>
               <ul>
                 <li>A ⇒ 単なる点変異だけでは 10<sup>10</sup> の多様性は説明できない。</li>
                 <li>C ⇒ スプライシングは主にクラス（IgM/IgD等）の切り替えに関与するが、可変領域の多様性の主因ではない。</li>
                 <li>D ⇒ 抗原が遺伝子を『書き加える』ことはない（鋳型説的な誤り）。</li>
               </ul>
               <p class="tiny">この「遺伝子は組み換わる」という発見で、利根川進が
               1987年のノーベル生理学・医学賞を受賞しました。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm11", ...CAT,
    title: "自己寛容と自己免疫疾患", diff: 295, tags: ["自己寛容", "制御性T細胞", "自己免疫疾患", "中枢性寛容"],
    explain: `<p>免疫は本来、自分自身の成分（自己抗原）を攻撃しない。この性質を<b>自己寛容</b>という。
              自己寛容と、それが破綻したときの自己免疫疾患について、正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "自己を攻撃するリンパ球は、そもそも一切作られないので除去する仕組みは不要である。",
      "胸腺で自己に強く反応するT細胞が除去され（負の選択）、末梢では制御性T細胞などが自己反応を抑える。破綻すると自己免疫疾患になる。",
      "自己免疫疾患は、免疫が弱くなって病原体を攻撃できなくなる病気である。",
      "自己寛容は生まれつき完成しており、後天的に破綻することは決してない。",
    ],
    answer: 1,
    hints: [
      "自己反応性の細胞は『作られてしまう』が、その後どう処理されるかがポイントです。",
      "胸腺（中枢）と末梢の2段階でチェックしています。",
    ],
    solution: `<p><b>【ステップ1】自己反応性リンパ球は生じてしまう</b></p>
               <p>V(D)J組換えで受容体をランダムに作るため、
               <b>自己を攻撃してしまう受容体</b>も一定の確率で生まれます。
               そこで体は、それらを取り除く／抑える仕組みを何段も用意しています。</p>
               <p><b>【ステップ2】二段構えのチェック</b></p>
               <table>
                 <tr><th>段階</th><th>場所</th><th>仕組み</th></tr>
                 <tr><td><b>中枢性寛容</b></td><td>胸腺（T細胞）・骨髄（B細胞）</td><td>自己に強く反応する細胞を<b>負の選択</b>で除去（アポトーシス）</td></tr>
                 <tr><td><b>末梢性寛容</b></td><td>全身の組織</td><td>すり抜けた細胞を<b>制御性T細胞（Treg）</b>やアナジーで抑制</td></tr>
               </table>
               <p><b>【ステップ3】破綻すると自己免疫疾患</b></p>
               <p>この仕組みが破綻すると、免疫が<b>自分の組織を攻撃</b>してしまいます。
               関節リウマチ、1型糖尿病、全身性エリテマトーデス（SLE）などが例です。
               つまり自己免疫疾患は「免疫が弱い」病気ではなく、
               <b>免疫が自己に向かって過剰／誤作動</b>する病気です（選択肢C が誤りな理由）。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "imm12", ...CAT,
    title: "I型アレルギー（即時型過敏症）", diff: 300, tags: ["アレルギー", "IgE", "肥満細胞", "ヒスタミン", "アナフィラキシー"],
    explain: `<p>花粉症や食物アレルギー、アナフィラキシーは<b>I型（即時型）アレルギー</b>に分類される。
              その発症の流れとして最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "初回の抗原（アレルゲン）侵入でIgEが作られ肥満細胞に結合、再度の侵入でIgEが架橋されて肥満細胞からヒスタミンなどが放出される。",
      "キラーT細胞が正常な自己細胞を直接攻撃することで、数日後に症状が出る。",
      "IgGと抗原が作る免疫複合体が血管壁に沈着して炎症を起こすのが主因で、数分以内に必ず全身反応が出る。",
      "アレルゲンに対して抗体がまったく作られないために、異物が排除できず症状が出る。",
    ],
    answer: 0,
    hints: [
      "『即時型』のカギは、あらかじめ細胞に結合して待機している抗体クラスです。",
      "1回目は準備（感作）、2回目で発作、という2段階を思い出しましょう。",
    ],
    solution: `<p><b>【ステップ1】感作（1回目）</b></p>
               <p>初めてアレルゲンに触れると、ヘルパーT細胞（Th2）の働きでB細胞が<b>IgE</b>を作ります。
               このIgEが<b>肥満細胞（マスト細胞）や好塩基球</b>の表面（FcεR受容体）に結合して待機します。
               この段階を<b>感作</b>といい、まだ症状は出ません。</p>
               <p><b>【ステップ2】発症（2回目以降）</b></p>
               <p>再び同じアレルゲンが入ると、
               肥満細胞上の<b>複数のIgEが抗原で架橋（橋渡し）</b>され、
               細胞が<b>脱顆粒</b>して<b>ヒスタミン</b>などを一気に放出します。
               これが血管拡張・平滑筋収縮・粘液分泌を引き起こし、
               <b>数分以内に</b>くしゃみ・じんましん・気道収縮などが現れます。
               全身に及ぶと<b>アナフィラキシー</b>です。</p>
               <p><b>【ステップ3】他の過敏症との区別</b></p>
               <table>
                 <tr><th>型</th><th>主役</th><th>例</th></tr>
                 <tr><td>I型（即時型）</td><td><b>IgE・肥満細胞</b></td><td>花粉症・食物アレルギー・アナフィラキシー</td></tr>
                 <tr><td>II型</td><td>IgG/IgMが細胞表面抗原に結合</td><td>不適合輸血・自己免疫性溶血性貧血</td></tr>
                 <tr><td>III型</td><td><b>免疫複合体</b>の沈着</td><td>血清病・糸球体腎炎（選択肢C）</td></tr>
                 <tr><td>IV型（遅延型）</td><td><b>T細胞</b>（数日後）</td><td>ツベルクリン反応・接触性皮膚炎（選択肢B）</td></tr>
               </table>
               <p>選択肢A はI型の流れを正しく述べています。B はIV型、C はIII型の説明で、
               D は誤り（むしろIgEが過剰に作られる）です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "imm13", ...CAT,
    title: "抗体価と希釈の計算", diff: 275, tags: ["抗体価", "希釈", "定量的推論", "計算"],
    explain: `<p>ある血清の<b>抗体価</b>を調べるため、血清を2倍ずつ段階希釈し、
              凝集反応が見られる<b>最も薄い希釈</b>を抗体価とする。</p>
              <p>原液を出発点に、2倍希釈を繰り返したところ、
              <b>1/64 まで</b>は凝集が見られ、<b>1/128 では</b>凝集が見られなかった。</p>
              <p>この血清の抗体価は「1: いくつ」と表されるか。分母の数字を半角で入力せよ（例：<code>32</code>）。</p>`,
    type: "text",
    answer: ["64", "1:64", "1/64"],
    hints: [
      "抗体価は『凝集が見られた中で最も薄い希釈』の希釈倍率です。",
      "1/2、1/4、1/8 …と2倍ずつ薄めます。凝集が出た最後は 1/64 でした。",
    ],
    solution: `<p><b>【ステップ1】抗体価の定義を確認</b></p>
               <p>抗体価は「反応（ここでは凝集）が認められる<b>最大の希釈倍率</b>」で表します。
               言い換えると、<b>陽性だった中で最も薄い希釈</b>です。</p>
               <p><b>【ステップ2】結果を並べる</b></p>
               <table>
                 <tr><th>希釈</th><th>1/2</th><th>…</th><th>1/32</th><th>1/64</th><th>1/128</th></tr>
                 <tr><td>凝集</td><td>＋</td><td>＋</td><td>＋</td><td><b>＋</b></td><td>−</td></tr>
               </table>
               <p>凝集が見られた最も薄い希釈は <b>1/64</b>、その次の 1/128 では陰性です。</p>
               <p><b>【ステップ3】表記</b></p>
               <p>よって抗体価は <b>1:64</b>（分母は <b>64</b>）と表されます。
               分母の数字が大きいほど、薄めても反応するほど抗体が多い＝<b>抗体価が高い</b>ことを意味します。</p>
               <p>したがって、答えは <b>64</b> です。</p>`,
  },
];
